import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingCart, Search, Menu, User, LogOut, Sparkles, ClipboardList, X } from "lucide-react";
import { useState } from "react";
import Logo from "./Logo";
import CategoriesMenu, { CATEGORY_MENU } from "./CategoriesMenu";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const Header = () => {
  const { count } = useCart();
  const { user, isAdmin, isCustomer, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const onSearch = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/shop?q=${encodeURIComponent(search.trim())}`);
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200" data-testid="site-header">
      <div className="bg-slate-900 text-white text-[11px] sm:text-xs py-1.5 text-center font-medium tracking-wide">
        Envíos a todo el país · Pedidos mayoristas por WhatsApp · Atención personalizada por tu vendedor
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-3 lg:gap-5">
        <div className="flex-shrink-0" data-testid="header-logo"><Logo size="md" /></div>

        <div className="hidden md:block"><CategoriesMenu /></div>

        <form onSubmit={onSearch} className="hidden md:flex flex-1 max-w-2xl relative group" data-testid="header-search-form">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            type="text"
            placeholder="Buscá por producto, marca o código..."
            className="w-full pl-11 pr-24 py-2.5 rounded-full bg-slate-100 border border-transparent focus:bg-white focus:border-green-500 focus:ring-4 focus:ring-green-100 outline-none transition-all"
            data-testid="header-search-input"
          />
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-green-600 transition-colors" />
          <button type="submit" className="absolute right-1.5 top-1/2 -translate-y-1/2 px-4 py-1.5 rounded-full bg-green-600 hover:bg-green-700 text-white text-xs font-semibold transition-colors" data-testid="header-search-submit">Buscar</button>
        </form>

        <nav className="hidden xl:flex items-center gap-6">
          <NavLink to="/" end className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`} data-testid="nav-home">Inicio</NavLink>
          <NavLink to="/shop" end className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`} data-testid="nav-shop">Tienda</NavLink>
          <NavLink to="/shop?novedades=1" className="nav-link inline-flex items-center gap-1 text-green-700" data-testid="nav-novedades"><Sparkles className="w-3.5 h-3.5" /> Novedades</NavLink>
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-2 ml-auto">
          {isAdmin && (
            <Link to="/admin" className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium text-blue-700 hover:bg-blue-50" data-testid="header-admin-link">
              <User className="w-4 h-4" /> Admin
            </Link>
          )}
          {isCustomer && (
            <>
              <Link to="/mis-pedidos" className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium text-slate-700 hover:bg-slate-100" data-testid="header-my-orders-link">
                <ClipboardList className="w-4 h-4" /> Mis pedidos
              </Link>
              <div className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium text-green-700 bg-green-50" data-testid="header-user-info">
                {user.welcome_discount_available && <Sparkles className="w-3.5 h-3.5 text-amber-500" />}
                <span className="max-w-[140px] truncate">Hola, {user.name.split(" ")[0]}</span>
              </div>
            </>
          )}
          {user ? (
            <button onClick={logout} className="p-2 rounded-full hover:bg-slate-100 text-slate-600" title="Salir" data-testid="header-logout-btn">
              <LogOut className="w-5 h-5" />
            </button>
          ) : (
            <>
              <Link to="/login" className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium text-slate-700 hover:bg-slate-100" data-testid="header-login-link">
                <User className="w-4 h-4" /> Ingresar
              </Link>
              <Link to="/register" className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors" data-testid="header-register-link">
                Registrarse
              </Link>
            </>
          )}
          <Link to="/cart" className="relative inline-flex items-center gap-2 pl-2.5 pr-3 py-2 rounded-full bg-green-600 hover:bg-green-700 text-white transition-colors shadow-sm" data-testid="header-cart-link">
            <ShoppingCart className="w-5 h-5" />
            <span className="hidden lg:inline text-sm font-semibold">Mi pedido</span>
            {count > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center ring-2 ring-white" data-testid="header-cart-count">
                {count}
              </span>
            )}
          </Link>
          <button className="md:hidden p-2 rounded-full hover:bg-slate-100" onClick={() => setMobileOpen((o) => !o)} aria-label="Menú" data-testid="mobile-menu-btn">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
          <div className="px-4 py-4 flex flex-col gap-3">
            <form onSubmit={onSearch} className="relative">
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar producto..." className="w-full pl-10 pr-4 py-2.5 rounded-full bg-slate-100 border border-transparent focus:bg-white focus:border-green-500 outline-none" data-testid="mobile-search-input" />
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            </form>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mt-1">Categorías</div>
            <div className="grid grid-cols-2 gap-2">
              {CATEGORY_MENU.map((c) => (
                <NavLink key={c.slug} to={c.to} onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-slate-200 text-sm font-medium" data-testid={`mobile-category-${c.slug}`}>
                  <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${c.color}`}><c.icon className="w-3.5 h-3.5" /></span>{c.name}
                </NavLink>
              ))}
            </div>
            <div className="flex flex-col divide-y divide-slate-100 mt-1">
              <NavLink to="/" end onClick={() => setMobileOpen(false)} className="py-2.5 font-medium">Inicio</NavLink>
              <NavLink to="/shop" onClick={() => setMobileOpen(false)} className="py-2.5 font-medium">Toda la tienda</NavLink>
              {isCustomer && <NavLink to="/mis-pedidos" onClick={() => setMobileOpen(false)} className="py-2.5 font-medium text-blue-600">Mis pedidos</NavLink>}
              {isAdmin && <NavLink to="/admin" onClick={() => setMobileOpen(false)} className="py-2.5 font-medium text-blue-600">Panel admin</NavLink>}
              {!user && <NavLink to="/login" onClick={() => setMobileOpen(false)} className="py-2.5 font-medium text-blue-600">Ingresar / Registrarse</NavLink>}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
